import React, { useContext, useState } from 'react';
import { useHistory } from 'react-router';
import { deleteData, fetchData } from '../../service/dataServices';
import { getTableMenuItems, pageSizeOptions } from '../../components/common-constants';
import { Button, Col, Divider, Dropdown, Input, Popconfirm, Row, Select, Space, Table, Typography } from 'antd';
import { DeleteOutlined, DownloadOutlined, DownOutlined, PlusOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import CustomContent from '../../../components/Content/CustomContent';
import { hasWritePermission } from '../../../components/Authorization/hasWritePermission';
import moment from 'moment';
import { useExcelExporter } from '../../components/export/useExcelExporter';
import { FilterContext } from '../../../App';

const { Search } = Input;
const { Text } = Typography;

const ShowInsurances = () => {
  const hasWritePerm = hasWritePermission('insurance');
  const history = useHistory();
  const { insuranceFilters: filters, setInsuranceFilters: setFilters } = useContext(FilterContext);

  const [selectedItem, setSelectedItem] = useState();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [data, setData] = useState([]);
  const { excelExportModal, onExport } = useExcelExporter('insurances', selectedRowKeys);

  const response = fetchData('/api/insurances', data => setData(data.content), filters);
  const deleteMutation = deleteData('/api/insurances');

  const onMenuClick = action => {
    if (action.key === 'edit' && selectedItem.id) {
      history.push('/save-insurance/' + selectedItem.id);
    }
  };

  const handleSingleDelete = () => {
    deleteMutation.mutate({ idList: selectedItem.id });
  };

  const handleTableChange = pagination => {
    if (pagination.current - 1 !== filters?.page) {
      setFilters(prevState => ({
        ...prevState,
        page: pagination.current - 1,
      }));
    }
    if (pagination.pageSize !== filters?.size) {
      setFilters(prevState => ({
        ...prevState,
        size: pagination.pageSize,
      }));
    }
  };

  const onSearch = value => {
    if (value !== '') {
      setFilters(prevState => ({
        ...prevState,
        searchText: value,
        page: 0,
      }));
    } else {
      const val = { ...filters };
      delete val.searchText;
      val.page = 0;
      setFilters(val);
    }
  };

  const onDelete = () => {
    deleteMutation.mutate({ idList: selectedRowKeys.join(',') });
    setSelectedRowKeys([]);
  };

  const onAdd = () => {
    history.push('/add-insurance');
  };

  const menu = getTableMenuItems(onMenuClick, handleSingleDelete);

  const columns = [
    {
      title: 'Inventory Number',
      dataIndex: 'inventoryNumber',
      key: 'inventoryNumber',
      render: (text, record) => (
        <>
          <Button
            type="link"
            onClick={() => history.push('/save-insurance/' + record.id)}
          >
            {text}
          </Button>
          <Text copyable={{ text: text }}></Text>
        </>
      ),
    },
    {
      title: 'Report ID',
      dataIndex: 'reportId',
      key: 'reportId',
    },
    {
      title: 'Recording Date',
      dataIndex: 'recordingDate',
      key: 'recordingDate',
      render: (text) => {
        return text ? moment(text).format('YYYY/MM/DD') : text;
      },
    },
    {
      title: 'Recorded By',
      dataIndex: 'recordedBy',
      key: 'recordedBy',
    },
    {
      title: 'Action',
      key: 'action',
      render: () => hasWritePerm ? (
        <span>
          <Dropdown overlay={menu} trigger={['click']}>
            <Button type="link">
              Actions <DownOutlined />
            </Button>
          </Dropdown>
        </span>
      ) : '',
    },
  ];

  return (
    <CustomContent>
      <Row>
        <Col span={16}>
          <Space direction="horizontal" style={{ width: '100%' }} wrap>
            <Search
              defaultValue={filters ? filters.searchText : ''}
              style={{ width: '30vh' }}
              placeholder="Search"
              onSearch={value => onSearch(value)}
              allowClear
            />
          </Space>
        </Col>
        <Col span={8}>
          <Row justify="end">
            <Space direction="horizontal">
              <Button
                onClick={onExport}
                icon={<DownloadOutlined />}
              >
                Export
              </Button>
              {hasWritePerm && (
                <>
                  <Popconfirm
                    title="Confirmation"
                    description="Are you sure to delete?"
                    icon={<QuestionCircleOutlined style={{ color: 'red' }} />}
                    onConfirm={onDelete}
                    okButtonProps={{ size: 'middle' }}
                    okText="Yes"
                    cancelButtonProps={{ size: 'middle' }}
                    styles={{ body: { padding: 16 } }}
                    disabled={selectedRowKeys.length === 0}
                  >
                    <Button
                      disabled={selectedRowKeys.length === 0}
                      icon={<DeleteOutlined />}
                    >
                      Delete
                    </Button>
                  </Popconfirm>
                  <Button onClick={onAdd} icon={<PlusOutlined />} type="primary">
                    Add
                  </Button>
                </>
              )}
            </Space>
          </Row>
        </Col>
      </Row>
      <Divider />
      <Table
        dataSource={data}
        columns={columns}
        loading={response.isFetching}
        rowKey={record => record.id}
        onChange={handleTableChange}
        size='middle'
        pagination={{
          pageSize: filters?.size,
          total: response.data ? response.data.data.totalElements : 0,
          showSizeChanger: true,
          current: filters ? filters.page + 1 : 1,
          pageSizeOptions: pageSizeOptions,
          showTotal: (total, range) => {
            return `${range[0]}-${range[1]} of ${total} items`;
          },
        }}
        rowSelection={{
          selectedRowKeys,
          onChange: selected => setSelectedRowKeys(selected),
        }}
        onRow={record => {
          return {
            onClick: () => setSelectedItem(record),
          };
        }}
      />
      {excelExportModal}
    </CustomContent>
  );
};

export default ShowInsurances;
